import React from 'react';
import { stateConfig, STATES } from 'lib/TutoringStateMachine/TutoringStateMachine';
import { compact, negate } from 'lodash';
import { InjectedRouter, PlainRoute, RouterState } from 'react-router';
import CurrentUser from 'auth/CurrentUser';
import {
  getStudentDetails,
  getUnauthedPurchaserDetails,
  getReservationInfo,
  getPurchaserRole,
  getPurchaseCompleted,
  getPurchaseResult,
  getLinkStudentCompleted,
} from 'lib/TutoringStateMachine/TutoringDataHelpers';
import { PurchaserRole } from 'globalTypes';

type TutoringViewProps = {
  goToNextState: () => void;
  router: InjectedRouter & RouterState;
  route: PlainRoute;
};

export type TutoringTopLevelComponent =
  | ((props: TutoringViewProps) => JSX.Element)
  | React.ComponentType<TutoringViewProps>;

export interface State {
  name: STATES;
  component: TutoringTopLevelComponent;
  location: string;
}

type StepDefinition = {
  step: STATES;
  isCompleted: () => boolean;
  //Right now there is only one step that can be skipped dynamically based on what the user
  //selects in previous steps, so shouldSkip will default to false when not defined.
  shouldSkip?: () => boolean;
};
const stateCompletion: { authed: StepDefinition[]; unauthed: StepDefinition[] } = {
  authed: [
    { step: STATES.GET_STARTED_AUTH, isCompleted: isGetStartedAuthCompleted },
    { step: STATES.SCHEDULE, isCompleted: isScheduleCompleted },
    { step: STATES.BILLING, isCompleted: isBillingCompleted },
    {
      step: STATES.LINK_STUDENT,
      isCompleted: isLinkStudentCompleted,

      shouldSkip: negate(shouldShowLinkStudent),
    },
    //we haven't decided how to handle clearing the data yet, for now the summary state is
    //never "completed"
    { step: STATES.SUMMARY, isCompleted: () => false },
  ],
  unauthed: [
    { step: STATES.GET_STARTED_UNAUTH, isCompleted: isGetStartedUnauthCompleted },
    { step: STATES.STUDENT_DETAILS_UNAUTH, isCompleted: isStudentDetailsCompleted },
    { step: STATES.SCHEDULE, isCompleted: isScheduleCompleted },
    { step: STATES.BILLING, isCompleted: isBillingCompleted },
    {
      step: STATES.LINK_STUDENT,
      isCompleted: isLinkStudentCompleted,
      shouldSkip: negate(shouldShowLinkStudent),
    },
    // todo: we haven't decided how to handle clearing the data yet, for now the summary state is
    // never "completed"
    { step: STATES.SUMMARY, isCompleted: () => false },
  ],
};

export class TutoringStateMachine {
  currentState: State;
  router: InjectedRouter & RouterState;

  constructor(router: InjectedRouter & RouterState) {
    this.router = router;
    let currentState;
    const stateForLocation = this.findStateForLocation(router.location.pathname);
    if (stateForLocation && this.stateIsAllowed(stateForLocation)) {
      currentState = stateForLocation;
    } else {
      currentState = this.getCompletionStatus().firstIncompleteState;
    }
    if (!currentState) {
      throw new Error(`Current state could not be found for ${router.location.pathname}`);
    }

    this.currentState = currentState;
    router.replace(currentState.location);
  }

  /** returns a list of all the completed states that apply to the user, and the next state
   for the user(ie, the first non-completed state in the list)
   We'll probably want to find a better name for what this returns than "state status" */
  getCompletionStatus() {
    const states = CurrentUser.isSignedIn() ? stateCompletion.authed : stateCompletion.unauthed;
    const completedStates: State[] = [];
    let firstIncompleteState: State | undefined = undefined;
    for (const state of states) {
      // get config would return the whole config object for the state(name, component, route, etc)
      if (state.isCompleted()) {
        completedStates.push(this.getConfigFor(state.step));
      } else if (!state.shouldSkip?.()) {
        firstIncompleteState = this.getConfigFor(state.step);
        break;
      }
    }
    return {
      completedStates,
      firstIncompleteState,
      get allowedStates() {
        return compact([...this.completedStates, this.firstIncompleteState]);
      },
    };
  }

  getConfigFor(state: STATES): State {
    return stateConfig[state];
  }

  goToNextState() {
    const nextState = this.nextStateOf(this.currentState);
    if (!this.stateIsAllowed(nextState)) {
      console.error(`state ${nextState.name} not allowed`);
      return;
    }
    this.currentState = nextState;
    this.router.push(nextState.location);
  }

  nextStateOf(state: State) {
    const steps = CurrentUser.isSignedIn() ? stateCompletion.authed : stateCompletion.unauthed;
    if (this.userTransitionedToSignedIn(state)) {
      return this.getConfigFor(STATES.GET_STARTED_AUTH);
    }

    const stateIndex = steps.findIndex(s => s.step === state.name);
    const remainingStates = steps.slice(stateIndex + 1);
    const nextState = remainingStates.find(s => !s.shouldSkip?.());
    if (!nextState) {
      throw new Error(`No next state found for ${this.currentState.name}`);
    }
    return this.getConfigFor(nextState.step);
  }

  private userTransitionedToSignedIn(state: State) {
    /* If the user signs in as part of device verifcation, we want to move them to the
      correct state for an authed user, instead of following the default behavior of
      moving to the next enabled step in the list. This is the check for whether that
      condition has happened.
     */
    return state.name === STATES.GET_STARTED_UNAUTH && CurrentUser.isSignedIn();
  }

  findStateForLocation(location: string) {
    const steps = CurrentUser.isSignedIn() ? stateCompletion.authed : stateCompletion.unauthed;

    for (const step of steps) {
      if (stateConfig[step.step].location === location) {
        return stateConfig[step.step];
      }
    }
    return undefined;
  }

  stateIsAllowed(state: State) {
    const stateStatus = this.getCompletionStatus();
    return stateStatus.allowedStates.some(s => s.name === state.name);
  }

  enabledStates(): State[] {
    const status = this.getCompletionStatus();
    return status.allowedStates;
  }
}

function isGetStartedAuthCompleted() {
  const studentDetails = getStudentDetails();
  return !!studentDetails;
}

function isGetStartedUnauthCompleted() {
  let getStartedData = getUnauthedPurchaserDetails();
  return (
    !!getStartedData &&
    allPresent(getStartedData, 'email', 'checkPolicies', 'fullName', 'purchaserRole')
  );
}

function isStudentDetailsCompleted() {
  const purchaserRole = getPurchaserRole();
  const studentDetails = getStudentDetails();
  const missingStudentName =
    purchaserRole === PurchaserRole.Parent && !studentDetails?.selectedChildName;
  return !!studentDetails && !missingStudentName;
}

function isScheduleCompleted() {
  let scheduleData = getReservationInfo();
  return !!scheduleData;
}

function isBillingCompleted() {
  const purchaseCompleted = getPurchaseCompleted();
  const purchaseResult = getPurchaseResult();
  return purchaseCompleted && !!purchaseResult;
}

function isLinkStudentCompleted() {
  return getLinkStudentCompleted();
}

function shouldShowLinkStudent() {
  if (CurrentUser.isSignedIn()) {
    return !CurrentUser.getUser().isStudent();
  } else {
    const linkStudentToken = getPurchaseResult()?.linkStudentToken;
    const selectedRole = getPurchaserRole();
    // If the purchase is successful but there is an error when trying to
    // create the link student token, the purchase endpoint will return successfully,
    // but bu the response will not have a token.
    return selectedRole === PurchaserRole.Parent && linkStudentToken;
  }
}

/* Returns true when all the received properties for an object are "present".
  a property is considered to be "present" when it is not null, undefined, or the
  empty string.
 */
function allPresent<T extends {}, Keys extends keyof T>(object: T, ...properties: Keys[]) {
  if (!object) {
    return false;
  }
  for (const prop of properties) {
    if (object[prop] === null || object[prop] === undefined || (object[prop] as any) === '') {
      return false;
    }
  }
  return true;
}
